import { useState } from 'react';
import type { ReviewLabel } from '../api/types';

interface Props {
  labels: ReviewLabel[];
  value: string;
  onChange: (label: string) => void;
  /** Rendered on a dark toolbar vs the light page footer. */
  tone?: 'light' | 'dark';
  disabled?: boolean;
  'data-testid'?: string;
}

const CREATE = '__create__';

/**
 * Pick an existing review label or create a new one.
 *
 * A <select> of known labels plus a "+ New label…" entry that swaps in a text input.
 * Deliberately NOT pre-filled with `reviewed-{today}`: a date-stamped default breeds
 * a new near-duplicate label every day, which makes the filter dropdown useless within
 * a week. The caller persists the last choice instead, so returning tomorrow needs no
 * setup at all.
 */
export function LabelPicker({
  labels, value, onChange, tone = 'light', disabled, ...rest
}: Props) {
  // creating when the operator picked "+ New label…", or when the remembered label is
  // no longer in the store's list (it was fully removed) — otherwise the <select>
  // would silently snap to some other label and mislabel the next batch.
  const known = labels.some((l) => l.label === value);
  const [creating, setCreating] = useState(false);
  const showInput = creating || (Boolean(value) && !known);

  // Match whatever sits next to it. In the dark toolbar that's the `px-3 py-1 rounded`
  // action buttons; in the page footer it's ff-btn-ghost (rounded-full, py-2.5), and
  // the previous square `rounded` control read as a stray form field between pills.
  const field =
    tone === 'dark'
      ? 'px-3 py-1 rounded bg-ff-ivory text-ff-forest text-sm border border-transparent ' +
        'focus:outline-none focus:ring-2 focus:ring-ff-ivory/40'
      : 'px-4 py-2.5 rounded-full border border-ff-sand/60 bg-white text-sm text-ff-forest ' +
        'hover:border-ff-forest/40 focus:outline-none focus:ring-2 focus:ring-ff-forest/20 ' +
        'transition-colors';

  if (showInput) {
    return (
      <input
        type="text"
        autoFocus={creating}
        value={value}
        disabled={disabled}
        maxLength={64}
        placeholder="new label…"
        aria-label="New review label"
        data-testid={rest['data-testid'] ?? 'label-picker-input'}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setCreating(false)}
        className={field + ' w-44 placeholder:text-ff-mute/60'}
      />
    );
  }

  return (
    <select
      value={value}
      disabled={disabled}
      aria-label="Review label"
      data-testid={rest['data-testid'] ?? 'label-picker'}
      onChange={(e) => {
        if (e.target.value === CREATE) {
          setCreating(true);
          onChange('');
        } else {
          onChange(e.target.value);
        }
      }}
      className={field}
    >
      {!value && <option value="">select a label…</option>}
      {labels.map((l) => (
        <option key={l.label} value={l.label}>
          {l.label} ({l.count})
        </option>
      ))}
      <option value={CREATE}>+ New label…</option>
    </select>
  );
}
